import React, { useImperativeHandle, useState, forwardRef } from 'react'
import ReactDOM from 'react-dom'
import { UseModelContext } from 'umi'
import { BrowserRouter } from 'react-router-dom'
import { ConfigProvider, Modal } from 'antd'
import { Provider } from 'react-redux'
import zhCN from 'antd/es/locale/zh_CN'
import { getStore } from '@/utils/dva'
import ErrorComponent from '@/components/ErrorComponent'

const _CreateApiDestroyFns = []

export const CreateApiDestroy = () => {
    while (_CreateApiDestroyFns.length) {
        let destroy = _CreateApiDestroyFns.pop()

        if (destroy) {
            destroy()
        }
    }
}

export const createAPI = (Component, options) => {
    const config = {
        visible: false,
        destroyOnClose: true,
        ...(options || {})
    }

    let $root = document.createElement('div')

    let currentConfig = {
        ...config,
        visible: true,
        onCancel(props) {
            close()

            config.onCancel && config.onCancel(props)
        }
    }

    if (typeof config.getCustomContainer === 'function') {
        const $element = config.getCustomContainer()
        $element?.appendChild($root)
    } else {
        document.body.appendChild($root)
    }

    function render(props) {
        ReactDOM.render(
            <ErrorComponent hideFallbackContent anchor="createAPI FallbackComponent">
                <ConfigProvider locale={zhCN}>
                    <Provider store={getStore()}>
                        <BrowserRouter>
                            <UseModelContext isCreateAPI>
                                <Component {...props} />
                            </UseModelContext>
                        </BrowserRouter>
                    </Provider>
                </ConfigProvider>
            </ErrorComponent>,
            $root
        )
    }

    function destroy(...args) {
        const unmountResult = ReactDOM.unmountComponentAtNode($root)
        if (unmountResult && $root.parentNode) {
            $root.parentNode.removeChild($root)
        }

        const triggerCancel = args.some(param => param && param.triggerCancel)
        if (currentConfig.onCancel && triggerCancel) {
            currentConfig.onCancel(...args)
        }
    }

    function update(newConfig) {
        currentConfig = {
            ...currentConfig,
            ...newConfig
        }

        render(currentConfig)
    }

    function close(...args) {
        const { afterClose, destroyOnClose } = currentConfig

        currentConfig = {
            ...currentConfig,
            visible: false,
            afterClose() {
                if (destroyOnClose) {
                    destroy.call(this, ...args) // 注意：需要把afterClose属性记载Modal上
                }

                afterClose && afterClose()
            }
        }

        render(currentConfig)
    }

    if (config.routerChangeClose) {
        _CreateApiDestroyFns.push(destroy)
    }

    render(currentConfig)

    return {
        update,
        close,
        destroy
    }
}

export const refAPI = (Component, config = {}) => {
    return forwardRef((_, ref) => {
        const [ctrl, setCtrl] = useState({
            visible: false
        })

        useImperativeHandle(ref, () => ({
            setCtrl
        }))

        const props = {
            ...ctrl,
            onCancel() {
                setCtrl(prev => ({
                    ...prev,
                    visible: false
                }))
            }
        }

        return <Component {...config} {...props} />
    })
}

/**
 * 创建promise的方式打开弹窗
 * @param {*} Component 组件
 * @param {*} extra 额外属性
 * @param {*} extra.resolveKey 确定键
 * @param {*} extra.rejectKey 取消键
 * @param {*} extra.beforeResolveKey resolve前执行的方法
 * @returns () => Promise
 */
export const promiseAPI = (
    Component,
    extra = { resolveKey: 'onOk', rejectKey: 'onCancel', beforeResolveKey: 'wainFunc' }
) => {
    return (options = {}) =>
        new Promise((resolve, reject) => {
            const { resolveKey, rejectKey, beforeResolveKey } = extra

            const modal = createAPI(Component, {
                routerChangeClose: true,
                ...(options || {}),
                async [resolveKey](e) {
                    try {
                        let waitRes = null

                        if (typeof options[beforeResolveKey] === 'function') {
                            const res =
                                typeof options.resolveWaitRes === 'function'
                                    ? options[beforeResolveKey](
                                          options.resolveWaitRes(e, {
                                              close: modal?.close,
                                              reject
                                          })
                                      )
                                    : options[beforeResolveKey]() //  注意：不能加加参数

                            //  boolean
                            if (typeof res === 'boolean') {
                                waitRes = res
                            }

                            // promise
                            if (res.then) {
                                waitRes = await res
                            }
                        }

                        modal?.close()
                        resolve({
                            data: e,
                            waitRes,
                            modal: {
                                close: modal?.close,
                                reject
                            }
                        })
                    } catch (e) {
                        console.error('catchError', e)
                        // 这里不reject好像是很多历史弹窗中onOk没有try catch，导致confirmLoading不会关闭
                    }

                    return Promise.resolve()
                },
                [rejectKey]() {
                    reject(new Error('cancel'))

                    return Promise.resolve()
                }
            })
        })
}

const DEFAULT_ZINDEX = 1100

/**
 * 获取当前弹窗最大z-index，临时解决弹窗层级问题
 * 貌似还是有问题，后面再查
 */
export const getCurrentModalMaxZindex = () => {
    const allEls = Array.from(document.querySelectorAll('.ant-modal-wrap')).concat(
        Array.from(document.querySelectorAll('.ant-drawer'))
    )

    const zIndexList = allEls
        .filter(Boolean)
        .map(v => {
            const elStyles = window.getComputedStyle(v)

            return Number(elStyles?.zIndex)
        })
        .filter(Boolean)

    console.info('getCurrentModalMaxZindex:', zIndexList)

    if (zIndexList.length) {
        const maxZindex = Math.max.apply(null, zIndexList)

        return maxZindex + 1
    }

    return DEFAULT_ZINDEX
}

// eslint-disable-next-line no-unused-vars
function isElementInViewport(el) {
    const rect = el.getBoundingClientRect()

    const windHeight = window.innerHeight || document.documentElement.clientHeight
    const winWidth = window.innerWidth || document.documentElement.clientWidth

    return (
        rect.top >= 0 &&
        rect.top < windHeight &&
        rect.left >= 0 &&
        rect.left < winWidth &&
        rect.bottom <= windHeight &&
        rect.right <= winWidth
    )
}

export const confirmModal = promiseAPI(props => {
    return <Modal title="提示" {...props} />
})
